import { Skeleton } from "@/components/ui/skeleton";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { captureCaughtException } from "@/lib/analytics";
import { isRoutineFailure } from "@/lib/errors";
import {
  autoCheckWorthRunning,
  claimableSuppliers,
  dismissTradeAccountCard,
  isOffline,
  isTradeAccountCardDismissed
} from "@/lib/order-assistant/trade-account-card";
import {
  CLAIM_EMAIL_IN_USE_API_MESSAGE,
  hasSupplierMatchAccount
} from "@/lib/supplier-match-status";
import { useProfile } from "@/queries/customer/customer";
import {
  useClaimSupplierMatch,
  useReadSupplierMatch,
  useSupplierMatchAutoCheck,
  useSupplierMatches
} from "@/queries/customer/supplier-match";
import { isFetchError, type SalonStockerHttpTypes } from "@repo/api-sdk";
import { cn } from "@repo/design-system/utils";
import { Button } from "@medusajs/ui";
import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { LINK_BUTTON_CLASS, LinkButton } from "./LinkButton";

type SupplierMatchView = SalonStockerHttpTypes.StoreSupplierMatchStatusView;

/** Suppliers listed before "Show N more", so a long list does not push the greeting off a phone. */
const VISIBLE_LIMIT = 4;

/** Why saving one supplier failed. */
type ClaimFailure =
  | "offline"
  | "unavailable"
  | "email_in_use"
  | "conflict"
  | "failed";

/** Where a single supplier's save stands. A supplier with no entry is untouched. */
type RowState =
  | { phase: "saving" }
  | { phase: "saved" }
  // Claimed, but the email still has to be confirmed with a click.
  | { phase: "confirm" }
  // The salon already has a live match with this supplier: nothing to save.
  | { phase: "already" }
  | { phase: "error"; failure: ClaimFailure };

const FAILURE_TEXT: Record<ClaimFailure, string> = {
  offline: "You're offline. Try again when you're back online.",
  unavailable: "This supplier can't take a trade account now.",
  email_in_use:
    "This email is already used by another account at this supplier. Use a different email.",
  conflict: "This one can't be saved here. Open Supplier Match to finish it.",
  failed: "We couldn't save this. Try again."
};

/** The failures whose fix is on the Supplier Match page. */
const FAILURES_WITH_LINK: ReadonlySet<ClaimFailure> = new Set([
  "email_in_use",
  "conflict"
]);

const DONE_TEXT = {
  saved: "Saved",
  confirm: "Check your email",
  already: "Already set up"
} as const;

const CLAIM_BOUNDARY = "order assistant trade account claim";

/**
 * Report a failed claim without the error itself: the API's message could
 * carry the salon's email, and ADR-0006 keeps request bodies out of events.
 * Only the status goes. Expected refusals (400, 409) and routine failures
 * (`isRoutineFailure`) say nothing about the app's health, and being offline
 * is not a fault.
 */
function reportClaimFailure(error: unknown): void {
  if (isOffline() || isRoutineFailure(error)) return;
  const status = isFetchError(error) ? error.status : undefined;
  if (status === 400 || status === 409) return;
  captureCaughtException(
    new Error(`Trade account claim failed${status ? ` (${status})` : ""}`),
    { boundary: CLAIM_BOUNDARY }
  );
}

function SupplierRow({
  view,
  state,
  onSave,
  onNavigate
}: {
  view: SupplierMatchView;
  state: RowState | undefined;
  onSave: (view: SupplierMatchView) => void;
  onNavigate: () => void;
}) {
  const doneText =
    state?.phase === "saved" ||
    state?.phase === "confirm" ||
    state?.phase === "already"
      ? DONE_TEXT[state.phase]
      : null;
  return (
    <li className="flex flex-col gap-y-1">
      <div className="flex items-center justify-between gap-x-3">
        <span
          title={view.seller_name}
          className="text-ui-fg-base min-w-0 truncate text-sm font-medium"
        >
          {view.seller_name}
        </span>
        <span aria-live="polite" className="shrink-0">
          {doneText ? (
            <span className="text-ui-fg-subtle flex items-center gap-x-1 text-sm">
              <Check className="h-4 w-4" aria-hidden="true" />
              {doneText}
            </span>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="small"
              isLoading={state?.phase === "saving"}
              disabled={state?.phase === "saving"}
              onClick={() => onSave(view)}
              aria-label={
                state?.phase === "saving"
                  ? `Saving ${view.seller_name}`
                  : `Save ${view.seller_name}`
              }
            >
              Save
            </Button>
          )}
        </span>
      </div>
      {state?.phase === "error" ? (
        <p role="alert" className="text-ui-fg-error text-xs">
          {FAILURE_TEXT[state.failure]}
        </p>
      ) : null}
      {state?.phase === "error" && FAILURES_WITH_LINK.has(state.failure) ? (
        <Link
          to="/account/supplier-match"
          onClick={onNavigate}
          className={cn(LINK_BUTTON_CLASS, "text-xs")}
        >
          Open Supplier Match
        </Link>
      ) : null}
    </li>
  );
}

/** The card's title and sentence for what the salon has done so far. */
function cardCopy(counts: {
  saved: number;
  confirm: number;
  already: number;
}): {
  title: string;
  body: string;
} {
  if (counts.saved > 0) {
    return {
      title: "Trade accounts saved",
      body:
        counts.confirm > 0
          ? "Trade prices show for suppliers that offer them here. Check your email to finish the others."
          : "Trade prices show for suppliers that offer them here."
    };
  }
  if (counts.confirm > 0) {
    return {
      title: "Almost done",
      body: "We sent a link to finish saving. Save more below, or finish."
    };
  }
  return {
    title: "Nothing to save",
    body: "You already have an account with that supplier. Save more below, or finish."
  };
}

const CARD_CLASS =
  "border-ui-border-base bg-ui-bg-base flex flex-col gap-y-3 rounded-lg border p-3 outline-none";

/** One line of text's height (`text-sm`), with a bar in it. */
function SkeletonLine({ className }: { className: string }) {
  return (
    <div className="flex h-5 items-center">
      <Skeleton className={cn("h-3", className)} />
    </div>
  );
}

/**
 * Holds the card's place while the auto-check runs, so the greeting and the
 * starter prompts do not move when the card arrives (a check on many suppliers
 * can take many seconds, and a tap that lands as they move would send a chat
 * message). Sized from the same pieces as the card for the suppliers it will
 * list. It has nothing to tap: no button-shaped blocks, so nobody aims at one.
 * If the check finds the salon already set up, it collapses.
 */
function CheckingPlaceholder({ supplierCount }: { supplierCount: number }) {
  return (
    <div role="status" className={CARD_CLASS}>
      <div className="space-y-1">
        <p className="text-ui-fg-subtle text-sm">Checking your suppliers…</p>
        <div>
          <SkeletonLine className="w-full" />
          <SkeletonLine className="w-full" />
          <SkeletonLine className="w-5/6" />
          <div className="sm:hidden">
            <SkeletonLine className="w-2/3" />
          </div>
        </div>
        <SkeletonLine className="w-32" />
      </div>
      <div aria-hidden="true" className="flex flex-col gap-y-2">
        {Array.from({ length: Math.min(supplierCount, VISIBLE_LIMIT) }).map(
          (_, index) => (
            <div key={index} className="flex h-7 items-center">
              <Skeleton className="h-3 w-40" />
            </div>
          )
        )}
      </div>
      {supplierCount > VISIBLE_LIMIT ? <SkeletonLine className="w-20" /> : null}
      <div aria-hidden="true" className="h-7" />
    </div>
  );
}

type ClaimableSuppliersCardProps = {
  customerId: string;
  loginEmail: string;
  /** The salon is sent to another page: close the drawer. */
  onNavigate: () => void;
};

/**
 * The card's body and its data. Only mounted for a salon whose profile is
 * loaded, and it reads nothing once the salon has said "Not now" today.
 */
function ClaimableSuppliersCard({
  customerId,
  loginEmail,
  onNavigate
}: ClaimableSuppliersCardProps) {
  const [hidden, setHidden] = useState(() =>
    isTradeAccountCardDismissed(customerId)
  );
  const list = useSupplierMatches({ enabled: !hidden });
  useReportLoadFailure(list.error, "order assistant supplier match list");
  const claimMatch = useClaimSupplierMatch();
  const readSupplierMatch = useReadSupplierMatch();

  const views = list.data?.supplier_matches;
  const listReady = !!views && !list.isFetching;
  const checkWanted = listReady && autoCheckWorthRunning(views);

  // "Zero matched" can mean the auto-check never ran: the Supplier Match page
  // runs it on open, so the card does too - but only when it could change what
  // the card shows, and at most once per page load (a lookup that times out
  // leaves its row unchecked, and re-running on every open would repeat live
  // supplier lookups). The card waits for the check request to settle, not
  // for rows to become settled. If the request fails, there is no card.
  const check = useSupplierMatchAutoCheck(customerId, {
    enabled: !hidden && checkWanted
  });
  // A failed check is reported where it runs, once (`useSupplierMatchAutoCheck`).
  const checkFailure = check.data?.ok === false;
  const checkSettled = check.isSuccess;
  const decided = listReady && (checkSettled || !checkWanted);

  // Decided once per mount, so a saved supplier (which leaves the list when it
  // refetches) does not pull the card away mid-use.
  const [latched, setLatched] = useState<SupplierMatchView[] | null>(null);
  useEffect(() => {
    if (decided && latched === null) {
      setLatched(checkFailure ? [] : claimableSuppliers(views));
    }
  }, [decided, latched, views, checkFailure]);
  // Used from the render that decides, not the one after the effect, so the
  // placeholder is replaced without an empty frame in between.
  const suppliers =
    latched ??
    (decided ? (checkFailure ? [] : claimableSuppliers(views)) : null);

  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [expanded, setExpanded] = useState(false);
  const cardRef = useRef<HTMLElement>(null);

  if (hidden) return null;
  if (suppliers === null && checkWanted) {
    return (
      <CheckingPlaceholder
        supplierCount={claimableSuppliers(views ?? []).length}
      />
    );
  }
  if (!suppliers || suppliers.length === 0) return null;

  const setRow = (sellerId: string, state: RowState) =>
    setRows((current) => ({ ...current, [sellerId]: state }));

  /** What a failed claim means for the row, from what the API answered. */
  const settleFailure = async (
    error: unknown,
    sellerId: string
  ): Promise<RowState> => {
    if (!isFetchError(error) || error.status === undefined) {
      return {
        phase: "error",
        failure: isOffline() ? "offline" : "failed"
      };
    }
    // 400: the supplier stopped taking claims.
    if (error.status === 400) {
      return { phase: "error", failure: "unavailable" };
    }
    // 409: a live match, or an email another account holds. The SDK's error
    // carries the message only, not the API's `code`. The email one is the
    // route's own fixed text; for any other 409 the grid says whether the
    // salon already has an account here. Anything else (a generic state
    // conflict) gets the general message and the page that can sort it out.
    if (error.status === 409) {
      if (error.message === CLAIM_EMAIL_IN_USE_API_MESSAGE) {
        return { phase: "error", failure: "email_in_use" };
      }
      const current = await readSupplierMatch(sellerId);
      return current && hasSupplierMatchAccount(current)
        ? { phase: "already" }
        : { phase: "error", failure: "conflict" };
    }
    return { phase: "error", failure: "failed" };
  };

  const save = async (view: SupplierMatchView) => {
    if (isOffline()) {
      setRow(view.seller_id, { phase: "error", failure: "offline" });
      return;
    }
    setRow(view.seller_id, { phase: "saving" });
    try {
      const response = await claimMatch.mutateAsync({
        sellerId: view.seller_id,
        email: loginEmail
      });
      setRow(view.seller_id, {
        phase:
          response.supplier_match.status === "claimed" ? "saved" : "confirm"
      });
      // The Save button the salon used is gone: keep focus inside the card.
      cardRef.current?.focus();
    } catch (error) {
      reportClaimFailure(error);
      setRow(view.seller_id, await settleFailure(error, view.seller_id));
    }
  };

  const phases = Object.values(rows).map((row) => row.phase);
  const counts = {
    saved: phases.filter((phase) => phase === "saved").length,
    confirm: phases.filter((phase) => phase === "confirm").length,
    already: phases.filter((phase) => phase === "already").length
  };
  const finished = counts.saved + counts.confirm + counts.already > 0;
  const copy = finished
    ? cardCopy(counts)
    : {
        title: "Save your trade accounts",
        body: null
      };
  const shown = expanded ? suppliers : suppliers.slice(0, VISIBLE_LIMIT);
  const hiddenCount = suppliers.length - shown.length;

  return (
    <section
      ref={cardRef}
      tabIndex={-1}
      aria-labelledby="trade-account-card-title"
      className={CARD_CLASS}
    >
      <div className="space-y-1">
        <h3
          id="trade-account-card-title"
          className="text-ui-fg-base text-sm font-semibold"
        >
          {copy.title}
        </h3>
        <p className="text-ui-fg-subtle text-sm">
          {copy.body ?? (
            <>
              Tap the suppliers where you have a trade account. We&apos;ll save
              them under{" "}
              <span className="text-ui-fg-base break-words">{loginEmail}</span>.
              Trade prices show for suppliers that offer them here.
            </>
          )}
        </p>
        <Link
          to="/account/supplier-match"
          onClick={onNavigate}
          className={LINK_BUTTON_CLASS}
        >
          Use a different email
        </Link>
      </div>

      <ul className="flex flex-col gap-y-2">
        {shown.map((view) => (
          <SupplierRow
            key={view.seller_id}
            view={view}
            state={rows[view.seller_id]}
            onSave={save}
            onNavigate={onNavigate}
          />
        ))}
      </ul>

      {hiddenCount > 0 ? (
        <LinkButton onClick={() => setExpanded(true)}>
          Show {hiddenCount} more
        </LinkButton>
      ) : null}

      <div className="flex justify-end">
        <Button
          type="button"
          variant="transparent"
          size="small"
          onClick={() => {
            if (!finished) dismissTradeAccountCard(customerId);
            setHidden(true);
          }}
        >
          {finished ? "Done" : "Not now"}
        </Button>
      </div>
    </section>
  );
}

/**
 * The "save your trade accounts" card on a new chat (KAN-419): offered when the
 * salon has no claimed or matched supplier and at least one it can claim. Plain
 * UI - no chat turn, no model, and the model has no way to claim anything: the
 * salon taps each supplier, and that calls the same claim endpoint the Supplier
 * Match page uses, with the login email.
 */
export function TradeAccountCard({ onNavigate }: { onNavigate: () => void }) {
  const profile = useProfile();
  const customer = profile.data?.customer;
  if (!customer?.id || !customer.email) return null;
  return (
    <ClaimableSuppliersCard
      key={customer.id}
      customerId={customer.id}
      loginEmail={customer.email}
      onNavigate={onNavigate}
    />
  );
}
